import type { ChatModelRunResult } from "@assistant-ui/react";

type AssistantContent = NonNullable<ChatModelRunResult["content"]>;
type AssistantContentPart = AssistantContent[number];
type UrlSourceContentPart = Extract<AssistantContentPart, { type: "source"; sourceType: "url" }>;
type ActionLink = {
  title?: string;
  url: string;
};
type SourceRegistry = {
  actionLinksByUrl: Map<string, ActionLink>;
  renderedActionUrls: Set<string>;
  sourcesByUrl: Map<string, UrlSourceContentPart>;
};

const MAX_RESPONSE_SOURCES = 9;
const MAX_SOURCE_TITLE_LENGTH = 160;
const MARKDOWN_LINK_PATTERN = /\[([^\]\n]+)]\(([^)\s]+)(?:\s+(?:"[^"]*"|'[^']*'|\([^)]*\)))?\)/g;
const DIRECT_URL_KEYS = [
  "url",
  "source_url",
  "sourceUrl",
  "href",
  "link",
  "uri",
  "source_uri",
  "sourceUri",
  "application_url",
  "applicationUrl",
  "application_link",
  "applicationLink",
  "app_url",
  "appUrl",
  "app_link",
  "appLink",
  "apply_url",
  "applyUrl",
  "apply_link",
  "applyLink",
];
const TITLE_KEYS = ["title", "source_title", "sourceTitle", "label", "name"];

export function buildAssistantContentWithSources(
  text: string,
  options: { agentMetadata?: unknown; citations?: unknown } = {},
): AssistantContent {
  const registry = createSourceRegistry();

  appendSourceCitations(options.citations, registry);
  appendMetadataSources(options.agentMetadata, registry);
  appendEmbeddedJsonSources(text, registry);

  return buildSourceContent(text, registry);
}

function createSourceRegistry(): SourceRegistry {
  return {
    actionLinksByUrl: new Map<string, ActionLink>(),
    renderedActionUrls: new Set<string>(),
    sourcesByUrl: new Map<string, UrlSourceContentPart>(),
  };
}

function appendSourceCitations(raw: unknown, registry: SourceRegistry): void {
  if (!Array.isArray(raw)) return;

  for (const citation of raw) {
    appendSourceCitation(citation, registry);
    if (registry.sourcesByUrl.size >= MAX_RESPONSE_SOURCES) break;
  }
}

function appendSourceCitation(citation: unknown, registry: SourceRegistry): void {
  const title = readCitationTitle(citation);
  appendUrlCitation({ url: readCitationUrl(citation), title }, registry);

  if (!isRecord(citation) || !Array.isArray(citation.retrievedReferences)) return;
  for (const reference of citation.retrievedReferences) {
    appendUrlCitation(
      {
        url: readCitationUrl(reference),
        title: readCitationTitle(reference) ?? title,
      },
      registry,
    );
    if (registry.sourcesByUrl.size >= MAX_RESPONSE_SOURCES) return;
  }
}

function appendMetadataSources(metadata: unknown, registry: SourceRegistry): void {
  if (!isRecord(metadata)) return;

  for (const key of [
    "citations",
    "sources",
    "source_urls",
    "sourceUrls",
    "child_response_sources",
    "childResponseSources",
    "application_links",
    "applicationLinks",
    "app_links",
    "appLinks",
    "apply_links",
    "applyLinks",
  ]) {
    appendSourceCitations(asSourceCitationArray(metadata[key]), registry);
    if (registry.sourcesByUrl.size >= MAX_RESPONSE_SOURCES) return;
  }

  appendSourceCitations([metadata], registry);
}

function appendEmbeddedJsonSources(text: string, registry: SourceRegistry): void {
  const payload = parseJsonObjectFromText(text);
  if (!payload) return;

  appendSourceCitations(asSourceCitationArray(payload.citations), registry);
  appendSourceCitations(asSourceCitationArray(payload.sources), registry);
}

function parseJsonObjectFromText(text: string): Record<string, unknown> | null {
  const trimmed = unwrapJsonCodeFence(text.trim());
  if (!trimmed.startsWith("{") || !trimmed.endsWith("}")) return null;

  try {
    const parsed = JSON.parse(trimmed) as unknown;
    return isRecord(parsed) ? parsed : null;
  } catch {
    return null;
  }
}

function unwrapJsonCodeFence(text: string): string {
  const match = text.match(/^```(?:json)?\s*([\s\S]*?)\s*```$/i);
  return match?.[1]?.trim() ?? text;
}

function asSourceCitationArray(value: unknown): unknown[] {
  if (Array.isArray(value)) return value;
  if (typeof value === "string" || isRecord(value)) return [value];
  return [];
}

function appendUrlCitation(
  citation: { url: unknown; title?: unknown },
  registry: SourceRegistry,
): UrlSourceContentPart | ActionLink | null {
  const url = normalizeCitationUrl(citation.url);
  if (!url) return null;

  const title = cleanSourceString(citation.title, MAX_SOURCE_TITLE_LENGTH);
  if (isActionLink({ url, title })) {
    const actionLink = { url, ...(title ? { title } : {}) };
    registry.actionLinksByUrl.set(url, actionLink);
    return actionLink;
  }

  return getOrCreateUrlSource({ url, title }, registry);
}

function getOrCreateUrlSource(
  citation: { url: unknown; title?: unknown },
  registry: SourceRegistry,
): UrlSourceContentPart | null {
  const url = normalizeCitationUrl(citation.url);
  if (!url) return null;

  const title = cleanSourceString(citation.title, MAX_SOURCE_TITLE_LENGTH);
  const existing = registry.sourcesByUrl.get(url);
  if (existing) {
    if (title && !existing.title) {
      const updated = { ...existing, title };
      registry.sourcesByUrl.set(url, updated);
      return updated;
    }
    return existing;
  }

  if (registry.sourcesByUrl.size >= MAX_RESPONSE_SOURCES) return null;

  const source: UrlSourceContentPart = {
    type: "source",
    sourceType: "url",
    id: String(registry.sourcesByUrl.size + 1),
    url,
    ...(title ? { title } : {}),
  };
  registry.sourcesByUrl.set(url, source);
  return source;
}

function readCitationUrl(citation: unknown): unknown {
  if (typeof citation === "string") return citation;
  if (!isRecord(citation)) return undefined;

  const directUrl = pickRecordValue(citation, DIRECT_URL_KEYS);
  if (directUrl) return directUrl;

  const metadataUrl = isRecord(citation.metadata)
    ? pickRecordValue(citation.metadata, DIRECT_URL_KEYS)
    : undefined;
  if (metadataUrl) return metadataUrl;

  return (
    readCitationLocationUrl(citation.location) ??
    readCitationLocationUrl(citation.retrievedReference) ??
    readCitationReferencesUrl(citation.retrievedReferences)
  );
}

function readCitationLocationUrl(value: unknown): unknown {
  if (!isRecord(value)) return undefined;

  const directUrl = pickRecordValue(value, ["url", "uri"]);
  if (directUrl) return directUrl;

  const webLocation = isRecord(value.webLocation) ? pickRecordValue(value.webLocation, ["url", "uri"]) : undefined;
  if (webLocation) return webLocation;

  const s3Location = isRecord(value.s3Location) ? pickRecordValue(value.s3Location, ["uri"]) : undefined;
  if (s3Location) return s3Location;

  return readCitationLocationUrl(value.location);
}

function readCitationReferencesUrl(value: unknown): unknown {
  if (!Array.isArray(value)) return undefined;

  for (const reference of value) {
    const url = readCitationLocationUrl(reference);
    if (url) return url;
  }

  return undefined;
}

function readCitationTitle(citation: unknown): unknown {
  if (!isRecord(citation)) return undefined;

  return (
    pickRecordValue(citation, TITLE_KEYS) ??
    (isRecord(citation.metadata)
      ? pickRecordValue(citation.metadata, TITLE_KEYS)
      : undefined)
  );
}

function pickRecordValue(record: Record<string, unknown>, keys: string[]): unknown {
  for (const key of keys) {
    if (record[key] != null) return record[key];
  }
  return undefined;
}

function normalizeCitationUrl(value: unknown): string | null {
  if (typeof value !== "string") return null;
  const trimmed = value.trim();
  if (!trimmed) return null;

  try {
    const url = new URL(trimmed);
    if (url.protocol !== "http:" && url.protocol !== "https:") return null;
    return url.toString();
  } catch {
    return null;
  }
}

function cleanSourceString(value: unknown, maxLength: number): string {
  if (typeof value !== "string") return "";
  const trimmed = value.trim();
  if (!trimmed) return "";
  return trimmed.slice(0, maxLength);
}

function buildSourceContent(text: string, registry: SourceRegistry): AssistantContent {
  const textWithActionLinks = injectActionLinks(text, registry);
  const cleanedText = extractMarkdownLinkSources(textWithActionLinks, registry);
  Array.from(registry.actionLinksByUrl.values())
    .filter((source) => !registry.renderedActionUrls.has(source.url))
    .forEach((source) => getOrCreateUrlSource({ url: source.url, title: source.title }, registry));
  const sources = Array.from(registry.sourcesByUrl.values());

  return sources.length > 0
    ? [{ type: "text", text: cleanedText }, ...buildSourceGroupParts(sources, "source-group-response")]
    : [{ type: "text", text: cleanedText }];
}

function extractMarkdownLinkSources(
  text: string,
  registry: SourceRegistry,
): string {
  return text.replace(MARKDOWN_LINK_PATTERN, (match, rawLabel: string, rawUrl: string) => {
    const label = rawLabel.trim();
    const url = normalizeCitationUrl(rawUrl);
    if (url && isActionLink({ url, title: label })) {
      registry.renderedActionUrls.add(url);
      return match;
    }
    const source = getOrCreateUrlSource({ url: rawUrl, title: label }, registry);
    if (!source) return label || match;
    return label || source.url;
  });
}

function injectActionLinks(text: string, registry: SourceRegistry): string {
  let linkedText = text;
  for (const actionLink of registry.actionLinksByUrl.values()) {
    if (
      registry.renderedActionUrls.has(actionLink.url) ||
      textIncludesMarkdownUrl(linkedText, actionLink.url) ||
      markdownActionLinkContainsPhrase(linkedText, actionLink)
    ) {
      registry.renderedActionUrls.add(actionLink.url);
      continue;
    }

    const nextText = linkFirstActionPhrase(linkedText, actionLink);
    if (nextText !== linkedText) {
      registry.renderedActionUrls.add(actionLink.url);
      linkedText = nextText;
    }
  }
  return linkedText;
}

function linkFirstActionPhrase(text: string, actionLink: ActionLink): string {
  for (const pattern of actionLinkPatterns(actionLink)) {
    const linkedText = replaceFirstOutsideMarkdownLinks(
      text,
      pattern,
      (match) => `[${match}](${actionLink.url})`,
    );
    if (linkedText !== text) return linkedText;
  }
  return text;
}

function replaceFirstOutsideMarkdownLinks(
  text: string,
  pattern: RegExp,
  replacement: (match: string) => string,
): string {
  MARKDOWN_LINK_PATTERN.lastIndex = 0;
  let cursor = 0;
  let markdownMatch: RegExpExecArray | null;
  while ((markdownMatch = MARKDOWN_LINK_PATTERN.exec(text)) !== null) {
    const plainText = text.slice(cursor, markdownMatch.index);
    const linkedPlainText = plainText.replace(pattern, replacement);
    if (linkedPlainText !== plainText) {
      MARKDOWN_LINK_PATTERN.lastIndex = 0;
      return text.slice(0, cursor) + linkedPlainText + text.slice(markdownMatch.index);
    }
    cursor = markdownMatch.index + markdownMatch[0].length;
  }
  MARKDOWN_LINK_PATTERN.lastIndex = 0;
  const trailingText = text.slice(cursor);
  const linkedTrailingText = trailingText.replace(pattern, replacement);
  return linkedTrailingText === trailingText
    ? text
    : text.slice(0, cursor) + linkedTrailingText;
}

function markdownActionLinkContainsPhrase(text: string, actionLink: ActionLink): boolean {
  const patterns = actionLinkPatterns(actionLink);
  MARKDOWN_LINK_PATTERN.lastIndex = 0;
  let match: RegExpExecArray | null;
  while ((match = MARKDOWN_LINK_PATTERN.exec(text)) !== null) {
    const label = match[1].trim();
    const url = normalizeCitationUrl(match[2]);
    if (!url || !isActionLink({ url, title: label })) continue;
    if (patterns.some((pattern) => pattern.test(label))) {
      MARKDOWN_LINK_PATTERN.lastIndex = 0;
      return true;
    }
  }
  MARKDOWN_LINK_PATTERN.lastIndex = 0;
  return false;
}

function actionLinkPatterns(actionLink: ActionLink): RegExp[] {
  const patterns: RegExp[] = [];
  const kind = actionLinkKind(actionLink);
  if (actionLink.title && kind !== "leadership") {
    patterns.push(new RegExp(escapeRegExp(actionLink.title), "i"));
  }

  if (kind === "application") {
    patterns.push(
      /\bapply online\b/i,
      /\bapply now\b/i,
      /\bapply today\b/i,
      /\bapply to (?:university of phoenix|uopx|phoenix)\b/i,
      /\bstart(?: your| the)? application\b/i,
      /\bstart an application\b/i,
      /\bstart the [a-z0-9][a-z0-9 '&.-]{1,80}? application\b/i,
      /\bsubmit your application\b/i,
      /\bcomplete your application\b/i,
    );
  } else if (kind === "transcript") {
    patterns.push(
      /\brequest your transcripts?\b/i,
      /\brequest transcripts?\b/i,
      /\btranscript requests?\b/i,
    );
  } else if (kind === "leadership") {
    patterns.push(
      /\buniversity of phoenix leadership(?: history| team| page| information)?\b/i,
      /\bleadership(?: history| team| page| information)?\b/i,
    );
  } else if (kind === "communication-preferences") {
    patterns.push(
      /\bcommunication preferences\b/i,
      /\bcommunications preferences\b/i,
      /\bemail preferences\b/i,
      /\bunsubscribe\b/i,
      /\bopt out\b/i,
      /\bstop (?:emails?|messages?|communications?)\b/i,
    );
  } else if (kind === "request-info") {
    patterns.push(/\brequest info(?:rmation)?(?: now)?\b/i, /\benrollment representatives?\b/i);
  } else if (kind === "search") {
    patterns.push(/\bsearch tool\b/i);
  } else if (kind === "single-courses") {
    patterns.push(/\bsingle courses?\b/i, /\bindividual courses?\b/i);
  } else if (kind === "alumni-benefits") {
    patterns.push(/\balumni benefits?\b/i);
  } else if (kind === "savings-explorer") {
    patterns.push(/\bsavings explorer\b/i);
  } else if (kind === "behavioral-sciences") {
    patterns.push(/\bbehavioral sciences? programs?\b/i, /\bonline behavioral sciences? degrees?\b/i);
  }
  if (actionLink.title && kind === "leadership") {
    patterns.push(new RegExp(escapeRegExp(actionLink.title), "i"));
  }

  return patterns;
}

function isActionLink({ title, url }: { title?: string; url: string }): boolean {
  return actionLinkKind({ title, url }) !== null;
}

function actionLinkKind({
  title,
  url,
}: {
  title?: string;
  url: string;
}):
  | "application"
  | "transcript"
  | "leadership"
  | "communication-preferences"
  | "request-info"
  | "search"
  | "single-courses"
  | "alumni-benefits"
  | "savings-explorer"
  | "behavioral-sciences"
  | null {
  const value = `${title ?? ""} ${url}`.toLowerCase();
  const lowerUrl = url.toLowerCase();
  if (/\/apply(?:[/?#.]|$)/.test(lowerUrl)) return "application";
  if (/\bapplication\b/.test(value)) return "application";
  if (/\btranscripts?\b/.test(value)) return "transcript";
  if (/\/about\/leadership(?:[/?#.]|$)/.test(lowerUrl)) return "leadership";
  if (/\/request\/communication\/preferences(?:[/?#.]|$)/.test(lowerUrl)) {
    return "communication-preferences";
  }
  if (/\/request\/(?:m-)?request-information(?:[/?#.]|$)/.test(lowerUrl)) return "request-info";
  if (/\/search\.html(?:[/?#.]|$)/.test(lowerUrl)) return "search";
  if (/\/programs\/continuing-education\/individual-courses\.html(?:[/?#.]|$)/.test(lowerUrl)) {
    return "single-courses";
  }
  if (/\/alumni\.html(?:[/?#.]|$)/.test(lowerUrl)) return "alumni-benefits";
  if (/\/cost-savings\/ways-to-save(?:[/?#.]|$)/.test(lowerUrl)) return "savings-explorer";
  if (/\/online-behavioral-sciences-degrees\.html(?:[/?#.]|$)/.test(lowerUrl)) return "behavioral-sciences";
  return null;
}

function textIncludesMarkdownUrl(text: string, url: string): boolean {
  MARKDOWN_LINK_PATTERN.lastIndex = 0;
  let match: RegExpExecArray | null;
  while ((match = MARKDOWN_LINK_PATTERN.exec(text)) !== null) {
    if (normalizeCitationUrl(match[2]) === url) return true;
  }
  return false;
}

function escapeRegExp(value: string): string {
  return value.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");
}

function buildSourceGroupParts(
  sources: UrlSourceContentPart[],
  parentId: string,
): UrlSourceContentPart[] {
  const sourceGroupCount = sources.length;
  const sourceGroupSources = sources.map((source) => ({
    url: source.url,
    ...(source.title ? { title: source.title } : {}),
  }));

  return sources.map((source, sourceGroupIndex) => ({
    ...source,
    parentId,
    providerMetadata: {
      ...(source.providerMetadata ?? {}),
      phoebe: {
        sourceGroupCount,
        sourceGroupIndex,
        sourceGroupSources,
      },
    },
  }));
}

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === "object" && value !== null;
}
